/**
 * The destination's horizontal section bar, anchored to the app bar rather
 * than living inside each page (hybrid-3c §5.1): the place where you switch
 * section is the same pixel on every screen and does not scroll away.
 */

import React from 'react';
import { useTranslate } from 'react-admin';
import { Box, Tab, Tabs, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { APP_BAR_HEIGHT, useSectionTabs } from './useSectionTabs';

export const SectionTabs: React.FC = () => {
  const theme = useTheme();
  const translate = useTranslate();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { sections, tab, height } = useSectionTabs();

  if (sections.length === 0) return null;

  const value = sections.some((section) => section.key === tab?.key) ? tab!.key : false;

  return (
    <Box
      sx={{
        // Absolute inside the AppBar, so the strip spans exactly the content
        // column and shifts with it when the rail collapses.
        position: 'absolute',
        top: `${APP_BAR_HEIGHT}px`,
        left: 0,
        right: 0,
        height: `${height}px`,
        backgroundColor: theme.palette.background.appbar,
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: isMobile ? '0 14px' : '0 22px',
        boxSizing: 'border-box',
      }}
    >
      <Tabs
        value={value}
        onChange={(_, key: string) => {
          const target = sections.find((section) => section.key === key);
          if (target) navigate(target.to);
        }}
        variant="scrollable"
        scrollButtons={false}
        sx={{
          minHeight: `${height - 1}px`,
          '& .MuiTab-root': {
            minHeight: `${height - 1}px`,
            minWidth: 'auto',
            padding: '0 14px',
            fontSize: '0.84375rem',
            fontWeight: 500,
            color: theme.palette.text.secondary,
            '&.Mui-selected': {
              color: theme.palette.text.primary,
              fontWeight: 600,
            },
          },
        }}
      >
        {sections.map((section) => (
          <Tab key={section.key} value={section.key} label={translate(section.labelKey, { smart_count: 2 })} />
        ))}
      </Tabs>
    </Box>
  );
};

export default SectionTabs;
